/**
 * Chrome on top of media, placed relative to the MEDIA FRAME rather than to
 * the card.
 *
 * That distinction is the whole reason this exists as a primitive: badges
 * positioned against the card have to know the media's padding, so a change to
 * that padding silently moves every badge in the family. Here the offsets are
 * the frame's own, and the corners are logical, so a right-to-left surface
 * does not need a second set of them.
 */
"use client";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { CRC_MEDIA_SCRIM_IMAGE } from "./cresultcard.tokens";
import {
  CresultcardCorner,
  CresultcardOverlayReach,
} from "./cresultcard.types";

const CORNER_INSET = 8;
/** Half the content, so a straddling badge is centred on the frame's edge. */
const STRADDLE_TRANSFORM_TOP = "translateY(-50%)";
const STRADDLE_TRANSFORM_BOTTOM = "translateY(50%)";

const CORNER_POSITION: Record<CresultcardCorner, CSSProperties> = {
  [CresultcardCorner.TopStart]: {
    insetBlockStart: CORNER_INSET,
    insetInlineStart: CORNER_INSET,
  },
  [CresultcardCorner.TopEnd]: {
    insetBlockStart: CORNER_INSET,
    insetInlineEnd: CORNER_INSET,
  },
  [CresultcardCorner.BottomStart]: {
    insetBlockEnd: CORNER_INSET,
    insetInlineStart: CORNER_INSET,
  },
  [CresultcardCorner.BottomEnd]: {
    insetBlockEnd: CORNER_INSET,
    insetInlineEnd: CORNER_INSET,
  },
};

/**
 * The straddling variant keeps the inline inset and puts the block edge at
 * zero, then lifts the content by half its own height. The overlay is a
 * sibling of the clipped frame rather than a child of it, so the half that
 * hangs over the edge survives.
 */
const CORNER_STRADDLE: Record<CresultcardCorner, CSSProperties> = {
  [CresultcardCorner.TopStart]: {
    insetBlockStart: 0,
    insetInlineStart: CORNER_INSET,
    transform: STRADDLE_TRANSFORM_TOP,
  },
  [CresultcardCorner.TopEnd]: {
    insetBlockStart: 0,
    insetInlineEnd: CORNER_INSET,
    transform: STRADDLE_TRANSFORM_TOP,
  },
  [CresultcardCorner.BottomStart]: {
    insetBlockEnd: 0,
    insetInlineStart: CORNER_INSET,
    transform: STRADDLE_TRANSFORM_BOTTOM,
  },
  [CresultcardCorner.BottomEnd]: {
    insetBlockEnd: 0,
    insetInlineEnd: CORNER_INSET,
    transform: STRADDLE_TRANSFORM_BOTTOM,
  },
};

const CORNER_STYLE: Record<
  CresultcardOverlayReach,
  Record<CresultcardCorner, CSSProperties>
> = {
  [CresultcardOverlayReach.Inside]: CORNER_POSITION,
  [CresultcardOverlayReach.Straddle]: CORNER_STRADDLE,
};

/**
 * Dead centre of the frame, and pointer-transparent.
 *
 * The corners take clicks because a badge in a corner may be a control. The
 * centre must not: the frame beneath this overlay IS the control — it is what
 * carries `Play <title>` as its accessible name — and an element covering the
 * middle of it that swallowed clicks would disable the thing it advertises.
 * `reach` has no meaning here either, since straddling is defined as centring
 * on a block EDGE, which is the one thing this placement is not.
 */
const CENTRE_STYLE: CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
};

export type CresultcardOverlaySlots = Partial<
  Record<CresultcardCorner, ReactNode>
>;

interface CresultcardMediaOverlayProps {
  readonly slots: CresultcardOverlaySlots;
  /**
   * The one placement that is not a corner: a play mark, a duration capsule on
   * a poster carrying no other chrome, a state glyph over a map tile.
   *
   * It is a slot rather than a fifth member of `CresultcardCorner` because the
   * centre is not a corner in any of the ways that enum is used: it has no
   * inline inset to keep, no block edge to straddle, and `CresultcardCorner` is
   * published as "the four corners" and consumed as an exhaustive `Record` by
   * both placement tables above — a fifth member would force each of them to
   * invent an entry for a geometry they do not describe. Every pillar that
   * wanted this hand-positioned its own absolutely-inset span instead.
   */
  readonly centre?: ReactNode;
  /** A bottom-anchored scrim, so overlaid type stays readable on any photo. */
  readonly scrim?: boolean;
  /** Inside the frame, or centred on its block edge. */
  readonly reach?: CresultcardOverlayReach;
}

export function CresultcardMediaOverlay({
  slots,
  centre,
  scrim = false,
  reach = CresultcardOverlayReach.Inside,
}: CresultcardMediaOverlayProps): ReactElement {
  return (
    <span
      data-el="Cresultcard/MediaOverlay/Root"
      style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
    >
      {scrim && (
        <span
          aria-hidden
          data-el="Cresultcard/MediaOverlay/Scrim"
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: CRC_MEDIA_SCRIM_IMAGE,
          }}
        />
      )}
      {centre != null && (
        <span
          data-el="Cresultcard/MediaOverlay/Centre"
          style={CENTRE_STYLE}
        >
          {centre}
        </span>
      )}
      {Object.values(CresultcardCorner).map((corner) => {
        const content = slots[corner];
        if (content == null) {
          return null;
        }
        return (
          <span
            data-el="Cresultcard/MediaOverlay/Entry"
            key={corner}
            style={{
              position: "absolute",
              ...CORNER_STYLE[reach][corner],
              display: "inline-flex",
              alignItems: "center",
              // The scrim and the frame do not take clicks; a control placed in
              // a corner still has to.
              pointerEvents: "auto",
            }}
          >
            {content}
          </span>
        );
      })}
    </span>
  );
}
